import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import { Route, Switch } from 'react-router-dom';
import Loading from '../../../cisview/oldWebShared/aip/loading';
import { rootPath as corrMonitorPath, fallbackPath as assetViewPath } from '../../../assetView/redux/routing/util/routepath';
import { identify } from '../../../util/analytics/segment';
import {
	getCustomer,
	getUserName,
	getUserUUID,
	getEmail
} from '../../../util/user';
import { mustSelectCompany } from '../../../util/user/companySwitch';
import SwitchCompanyPage from '../CompanySwitch/SwitchCompanyPage';
import CachedRouteSwitch from './CachedRouteSwitch';

import {
	AppAccount,
	AppScanline,
	AppCISView,
	AppFieldLine,
	DataIntegrity,
	AppChargeLine,
	AppAssetView,
	AppLiveLine,
	AppVideoLine,
	AppTemplateBuilder,
	AppSettings,
	DashboardsAndReports,
	WebsiteBridge,
	Applications,
	ApiDocs,
	AllProjectsPage,
	MyProjectsPage,
	CorrMonitorPage,
	CorrMonitorProjectPage,
	AssetDetailPage,
	CorrLoaderPage,
	CorrLoaderJobPage,
	MyAccountPage,
	HelpSupportPage,
	SpectrumXLIPage,
	SpectrumXliDetailPage,
	TrackingPage,
	TrackingRunPage,
	AssetPhotoUploadPage
} from './LazyLoading';
import LeftPanel from '../LeftPanel';
import AccessDenied from '../shared/AccessDenied';

import {
	getApplications as acGetApplications,
	getCompanyProducts as acGetCompanyProducts
} from '../../redux/actions';
import { selectApplications, selectCompanyProducts } from '../../redux/selector';

const MainRouter = () => {
	const dispatch = useDispatch();
	const applications = useSelector(selectApplications);
	// Product-data flags for the company being viewed (null while unresolved).
	// CorrMonitor and Tracking are reachable on either the account's entitlement
	// or the viewed company's data, so both guards need this alongside
	// `applications` — otherwise the sidebar would offer a nav item whose route
	// answers Access Denied.
	const companyProducts = useSelector(selectCompanyProducts);
	// Every guard below is derived from `applications` (and the product flags), so
	// the router must not conclude a URL is unroutable before they have resolved.
	// The project links on All Projects are plain anchors (full page loads) and F5
	// does the same, so an unresolved list used to match no route and fall through
	// to the empty application shell — which paints a blank page.
	const [accessResolved, setAccessResolved] = useState(
		() => applications.length > 0 && companyProducts !== null
	);

	useEffect(() => {
		if (accessResolved) { return undefined; }
		let cancelled = false;
		const settle = () => {
			if (!cancelled) { setAccessResolved(true); }
		};
		// Both lookups resolve rather than reject, so the router always settles.
		Promise.all([
			Promise.resolve(dispatch(acGetApplications())),
			Promise.resolve(dispatch(acGetCompanyProducts()))
		]).then(settle, settle);
		return () => {
			cancelled = true;
		};
	}, [accessResolved, dispatch]);

	const appAccess = useMemo(() => {
		return applications.reduce((acc, app) => {
			acc[app.url.toLowerCase()] = app;
			return acc;
		}, {});
	}, [applications]);

	// One definition of "the product is reachable", shared with the sidebar
	// (see LeftPanel): entitled to it, or the viewed company has data for it.
	const hasCorrMonitor = useMemo(
		() =>
			Boolean(appAccess.corrmonitor) ||
			Boolean(companyProducts && (companyProducts.hasCp || companyProducts.hasXli)),
		[appAccess, companyProducts]
	);

	identify(getUserUUID(), {
		customer: getCustomer(),
		username: getUserName(),
		email: getEmail()
	});

	const pageRoutes = useMemo(() => [
		{ path: '/Account', component: AppAccount },

		// CORR LOADER — new pages (jobs list + read-only job overview)
		appAccess.corrloader && {
			exact: true,
			path: '/CorrLoader',
			component: CorrLoaderPage
		},
		appAccess.corrloader && {
			path: '/CorrLoader/overview/:job',
			component: CorrLoaderJobPage,
			getCacheKey: location => `/CorrLoader/overview${location.pathname}`
		},

		// CORR LOADER — legacy job management (list/create/edit)
		appAccess.corrloader && {
			path: '/CorrLoader/manage',
			component: AppCISView
		},

		// CORR LOADER — legacy editor (/CorrLoader/Job/...)
		appAccess.corrloader && {
			path: '/CorrLoader',
			component: AppCISView
		},
		appAccess.corrloader && {
			path: '/CISLine',
			component: AppCISView
		},

		// SPECTRUM XLI — in-line inspection surveys (cards + map).
		appAccess.corrloader && {
			exact: true,
			path: '/SpectrumXLI',
			component: SpectrumXLIPage
		},

		// CORR LOGIX
		appAccess.corrlogix && {
			path: '/CorrLogix',
			component: AppScanline
		},
		appAccess.corrlogix && {
			path: '/Scanline',
			component: AppScanline
		},

		// CORR LOGIX DATA
		appAccess.corrlogixdata && {
			path: '/CorrLogixData',
			component: AppFieldLine
		},
		appAccess.corrlogixdata && {
			path: '/Fieldline',
			component: AppFieldLine
		},
		appAccess.corrlogixdata && {
			path: '/FieldlineReact',
			component: AppFieldLine
		},

		// CORR MONITOR — CP project asset detail (nested).
		hasCorrMonitor && {
			path: '/CorrMonitor/CP/Project/:id/Asset/:assetId',
			component: AssetDetailPage,
			getCacheKey: location => location.pathname
		},

		// CORR MONITOR — single CP project details (scoped by project id).
		{
			path: '/CorrMonitor/CP/Project/:id',
			component: CorrMonitorProjectPage,
			getCacheKey: location => location.pathname
		},

		// SPECTRUM XLI — project details.
		{
			path: '/CorrMonitor/XLI/Project/:id',
			component: SpectrumXliDetailPage,
			getCacheKey: location => location.pathname
		},

		// CORR MONITOR — map + asset list (catch-all; keep last).
		hasCorrMonitor && {
			path: '/CorrMonitor/:zoom?/:lat?/:lon?',
			component: CorrMonitorPage,
			getCacheKey: () => '/CorrMonitor'
		},

		// CORR MONITOR — legacy assetView (now /CorrMonitorOld).
		appAccess.corrmonitor && {
			path: corrMonitorPath,
			component: AppAssetView
		},
		appAccess.corrmonitor && {
			path: assetViewPath,
			component: AppAssetView
		},

		// TRACKING (Armadillo LiveMap pig tracking).
		{
			path: '/Tracking/run/:runId',
			component: TrackingRunPage,
			getCacheKey: location => location.pathname
		},
		{
			path: '/Tracking/:zoom?/:lat?/:lon?',
			component: TrackingPage,
			getCacheKey: () => '/Tracking'
		},

		// Terminal routes for the guarded namespaces above.
		{ path: '/CorrLoader', component: AccessDenied, cache: false },
		{ path: '/SpectrumXLI', component: AccessDenied, cache: false },
		{ path: '/CorrMonitor', component: AccessDenied, cache: false },

		{ path: '/dataintegrity', component: DataIntegrity },
		appAccess.chargeline && {
			path: '/ChargeLine',
			component: AppChargeLine
		},
		appAccess.liveline && {
			path: '/LiveLine',
			component: AppLiveLine
		},
		appAccess.videoline && {
			path: '/VideoLine',
			component: AppVideoLine
		},
		{ path: '/templateBuilder', component: AppTemplateBuilder },
		{ path: '/CustomerUsers', component: AppSettings },
		{ path: '/AppList', component: Applications },
		{
			path: '/dashboards-and-reports',
			component: DashboardsAndReports
		},

		// Feature pages
		{ path: '/switch-company', component: SwitchCompanyPage },
		{ path: '/all-projects', component: AllProjectsPage },
		{ path: '/my-projects', component: MyProjectsPage },
		{ path: '/my-account', component: MyAccountPage },
		{ path: '/help-support', component: HelpSupportPage },

		// ADMIN — bulk CP asset photo upload.
		{ path: '/admin/asset-photos', component: AssetPhotoUploadPage },

		{ path: '/api-docs', component: ApiDocs },
		{ path: '/Index', component: WebsiteBridge },
		{ exact: true, path: '/', redirectTo: '/Applications', cache: false },
		window.hideReactApplcations === undefined && {
			path: '/',
			component: Applications
		}
	].filter(Boolean), [appAccess, hasCorrMonitor]);

	// Operators must pick a company before they can use the app. Until they do,
	// the whole app is replaced by the blocking company picker.
	if (mustSelectCompany()) {
		return <SwitchCompanyPage forced />;
	}

	// Rendered in both branches below: the shell (and its own applications
	// fetch) must stay mounted while the access list is resolving.
	const shellRoutes = (
		<Switch>
			<Route path="/Index" component={null} />
			<Route exact path="/" component={null} />
			<Route path="" component={LeftPanel} />
		</Switch>
	);

	if (!accessResolved) {
		return (
			<>
				<Loading title="Loading..." />
				{shellRoutes}
			</>
		);
	}

	return (
		<>
			<Suspense fallback={<Loading title="Loading..." />}>
				<CachedRouteSwitch routes={pageRoutes} />
			</Suspense>
			{shellRoutes}
		</>
	);
};

export default connect()(MainRouter);
